import type { LauraAudit } from "@/app/components/shared/types";
import { LauraStageShell } from "../shared/LauraStageShell";
import { StageSectionNav } from "../shared/StageSectionNav";
import { defaultP2FormData, type P2FormData } from "./p2Types";
import { useTranslation } from "@/app/i18n/LocaleContext";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Field, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Textarea } from "@/components/ui/textarea";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";

type Props = {
    audit: LauraAudit;
    deliverableContent: string;
    onDeliverableSaved: (content: string) => void;
    onStageStateChange?: (state: import("@/app/lib/auditflowApi").LauraStageState | null) => void;
};

const SECTIONS = [
    { id: "mandate", label: "Purpose & legal basis" },
    { id: "financial", label: "Financial profile" },
    { id: "org", label: "Organisational structure" },
    { id: "process", label: "Process map" },
    { id: "leakage", label: "Leakage vectors" },
    { id: "swot", label: "SWOT" },
    { id: "timeline", label: "Timeline" },
    { id: "risks", label: "Risk preview" },
];

export function LauraP2Workspace(props: Props) {
    const { t } = useTranslation();
    const [activeSection, setActiveSection] = useState("mandate");

    return (
        <LauraStageShell<P2FormData>
            audit={props.audit}
            stageId="p2"
            deliverableContent={props.deliverableContent}
            onDeliverableSaved={props.onDeliverableSaved}
            onStageStateChange={props.onStageStateChange}
            defaultForm={defaultP2FormData}
            title={t("laura", "p2Title")}
            description="Subject matter overview — legal, financial, process, and risk context"
            generateLabel={t("laura", "generateOverview")}
        >
            {({ form, setForm }) => (
                <div className="flex flex-col gap-4">
                    <StageSectionNav
                        sections={SECTIONS}
                        activeId={activeSection}
                        onSelect={setActiveSection}
                    />

                    {activeSection === "mandate" && (
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-sm">{t("laura", "p2_mandate")}</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <FieldGroup>
                                    <Field>
                                        <FieldLabel>Mandate</FieldLabel>
                                        <Textarea
                                            rows={3}
                                            value={form.mandate}
                                            onChange={(e) =>
                                                setForm((p) => ({ ...p, mandate: e.target.value }))
                                            }
                                        />
                                    </Field>
                                    {form.legal_instruments.length > 0 && (
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead>Instrument</TableHead>
                                                    <TableHead>Provision</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {form.legal_instruments.map((li, i) => (
                                                    <TableRow key={i}>
                                                        <TableCell>{li.norm}</TableCell>
                                                        <TableCell className="text-xs">
                                                            {li.provision}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    )}
                                </FieldGroup>
                            </CardContent>
                        </Card>
                    )}

                    {activeSection === "financial" && form.financial_years.length > 0 && (
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-sm">
                                    {t("laura", "p2_financialChart")}
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <FinancialBarChart data={form.financial_years} />
                                <ul className="mt-3 flex flex-col gap-1 text-xs text-muted-foreground">
                                    {form.financial_significance.map((s, i) => (
                                        <li key={i}>• {s}</li>
                                    ))}
                                </ul>
                            </CardContent>
                        </Card>
                    )}

                    {activeSection === "org" && (
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-sm">{t("laura", "p2_org")}</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <Textarea
                                    rows={8}
                                    className="font-mono text-xs"
                                    value={form.org_structure}
                                    onChange={(e) =>
                                        setForm((p) => ({
                                            ...p,
                                            org_structure: e.target.value,
                                        }))
                                    }
                                />
                            </CardContent>
                        </Card>
                    )}

                    {activeSection === "process" && (
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-sm">{t("laura", "p2_process")}</CardTitle>
                            </CardHeader>
                            <CardContent className="flex flex-col gap-3">
                                <Textarea
                                    rows={4}
                                    value={form.process_steps}
                                    onChange={(e) =>
                                        setForm((p) => ({
                                            ...p,
                                            process_steps: e.target.value,
                                        }))
                                    }
                                />
                                {form.control_points.length > 0 && (
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Control point</TableHead>
                                                <TableHead>Risk</TableHead>
                                                <TableHead>Control</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {form.control_points.map((cp, i) => (
                                                <TableRow key={i}>
                                                    <TableCell>{cp.process}</TableCell>
                                                    <TableCell className="text-xs">{cp.risk}</TableCell>
                                                    <TableCell className="text-xs">{cp.control}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                )}
                            </CardContent>
                        </Card>
                    )}

                    {activeSection === "leakage" &&
                        form.leakage_vectors.map((v, i) => (
                            <Card
                                key={i}
                                className={
                                    v.severity === "confirmed"
                                        ? "border-destructive/40"
                                        : "border-amber-400/40"
                                }
                            >
                                <CardHeader className="pb-2">
                                    <div className="flex items-center gap-2">
                                        <CardTitle className="text-sm">{v.vector}</CardTitle>
                                        <Badge
                                            variant={
                                                v.severity === "confirmed"
                                                    ? "destructive"
                                                    : "secondary"
                                            }
                                        >
                                            {v.severity}
                                        </Badge>
                                    </div>
                                </CardHeader>
                                <CardContent className="text-xs text-muted-foreground">
                                    <p>{v.description}</p>
                                    <p className="mt-1 font-medium text-foreground">
                                        Evidence: {v.evidence}
                                    </p>
                                </CardContent>
                            </Card>
                        ))}

                    {activeSection === "swot" && (
                        <SwotMatrix
                            swot={form.swot}
                            onChange={(swot) => setForm((p) => ({ ...p, swot }))}
                        />
                    )}

                    {activeSection === "timeline" && form.milestones.length > 0 && (
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-sm">Chronological context</CardTitle>
                            </CardHeader>
                            <CardContent className="flex flex-col gap-2">
                                {form.milestones.map((m, i) => (
                                    <div key={i} className="flex gap-3 text-sm">
                                        <Badge variant="outline">{m.year}</Badge>
                                        <div>
                                            <p className="font-medium">{m.event}</p>
                                            <p className="text-xs text-muted-foreground">
                                                {m.significance}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </CardContent>
                        </Card>
                    )}

                    {activeSection === "risks" && form.critical_risks_preview.length > 0 && (
                        <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
                            {form.critical_risks_preview.map((r, i) => (
                                <Card key={i}>
                                    <CardContent className="pt-4 text-xs">
                                        <Badge variant="outline" className="mb-1">
                                            {r.cluster}
                                        </Badge>
                                        <p className="font-medium">{r.area}</p>
                                        <p className="text-muted-foreground">{r.dimension}</p>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </LauraStageShell>
    );
}

function FinancialBarChart({
    data,
}: {
    data: { year: string; amount: string; growth: string }[];
}) {
    const max = Math.max(
        ...data.map((d) => parseFloat(d.amount.replace(/[^\d.]/g, "")) || 0),
        1,
    );
    return (
        <div className="flex h-36 items-end gap-2">
            {data.map((d) => {
                const val = parseFloat(d.amount.replace(/[^\d.]/g, "")) || 0;
                const h = Math.round((val / max) * 100);
                return (
                    <div key={d.year} className="flex flex-1 flex-col items-center gap-1">
                        <span className="text-[10px] text-muted-foreground">{d.growth}</span>
                        <div
                            className="w-full rounded-t bg-primary/80"
                            style={{ height: `${Math.max(h, 4)}%`, minHeight: 4 }}
                            title={`₹${d.amount} Cr`}
                        />
                        <span className="text-[10px]">{d.year}</span>
                    </div>
                );
            })}
        </div>
    );
}

function SwotMatrix({
    swot,
    onChange,
}: {
    swot: P2FormData["swot"];
    onChange: (s: P2FormData["swot"]) => void;
}) {
    const cells: {
        key: keyof P2FormData["swot"];
        label: string;
    }[] = [
        { key: "strengths", label: "Strengths" },
        { key: "weaknesses", label: "Weaknesses" },
        { key: "opportunities", label: "Opportunities" },
        { key: "threats", label: "Threats" },
    ];

    return (
        <div className="grid grid-cols-2 gap-2">
            {cells.map((c) => (
                <Card key={c.key}>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-xs">{c.label}</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <Textarea
                            rows={3}
                            className="text-xs"
                            value={swot[c.key].join("\n")}
                            onChange={(e) =>
                                onChange({
                                    ...swot,
                                    [c.key]: e.target.value.split("\n").filter(Boolean),
                                })
                            }
                        />
                    </CardContent>
                </Card>
            ))}
        </div>
    );
}
